import { useState, useEffect, useMemo } from 'react'
import { useRepos }                     from '@/tools/exmarked/hooks/useRepos'
import { usePlatformStore }             from '@/stores/platformStore'
import { useAppStore }                  from '@/tools/exmarked/stores/appStore'
import { RepoCard }                     from './RepoCard'
import type { ConnectedSource }         from '@/tools/exmarked/types'

// ─── GitHub Repo type (from API) ──────────────────────────────────────────────

interface GitHubRepo {
  id:             number
  name:           string
  full_name:      string
  private:        boolean
  description:    string | null
  default_branch: string
  updated_at:     string
  owner: {
    login:      string
    avatar_url: string
  }
}

// ─── Add GitHub Repo Modal ────────────────────────────────────────────────────

interface AddRepoModalProps {
  onClose: () => void
}

function AddRepoModal({ onClose }: AddRepoModalProps) {
  const session                             = usePlatformStore((s) => s.session)
  const { addRepo }                         = useRepos()
  const [ghRepos, setGhRepos]               = useState<GitHubRepo[]>([])
  const [isLoadingRepos, setIsLoadingRepos] = useState(true)
  const [fetchError, setFetchError]         = useState<string | null>(null)
  const [search, setSearch]                 = useState('')
  const [selected, setSelected]             = useState<GitHubRepo | null>(null)
  const [branch, setBranch]                 = useState('')
  const [isSaving, setIsSaving]             = useState(false)
  const [saveError, setSaveError]           = useState<string | null>(null)

  useEffect(() => {
    const token = session?.provider_token ?? localStorage.getItem('exmarked_gh_token')
    if (!token) {
      setFetchError('GitHub token unavailable — please sign out and sign in again.')
      setIsLoadingRepos(false)
      return
    }

    fetch('https://api.github.com/user/repos?per_page=100&sort=updated&type=all', {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept:        'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`GitHub API error: ${res.status}`)
        return res.json() as Promise<GitHubRepo[]>
      })
      .then((repos) => { setGhRepos(repos); setIsLoadingRepos(false) })
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : 'Failed to load repositories.')
        setIsLoadingRepos(false)
      })
  }, [session?.provider_token])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return ghRepos
    return ghRepos.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.owner.login.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
    )
  }, [ghRepos, search])

  const handleConnect = async () => {
    if (!selected) return
    setIsSaving(true)
    setSaveError(null)
    try {
      await addRepo(selected.owner.login, selected.name, {
        description:    selected.description ?? undefined,
        default_branch: branch.trim() || selected.default_branch,
        is_private:     selected.private,
        avatar_url:     selected.owner.avatar_url,
      })
      onClose()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to connect repository.')
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-forge-void/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-forge-basalt border border-forge-border rounded-xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-forge-border flex-shrink-0">
          <div>
            <h2 className="text-sm font-display font-semibold text-forge-primary uppercase tracking-wider">Connect Repository</h2>
            {!isLoadingRepos && !fetchError && (
              <p className="text-xs text-forge-ash mt-0.5">{ghRepos.length} repositories available</p>
            )}
          </div>
          <button onClick={onClose} className="text-forge-ash hover:text-forge-primary transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        {!fetchError && (
          <div className="px-4 py-3 border-b border-forge-border/50 flex-shrink-0">
            <div className="flex items-center gap-2 bg-forge-void rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-forge-ash flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search repositories…"
                className="bg-transparent text-sm text-forge-primary placeholder-forge-ash outline-none w-full"
                autoFocus
                disabled={isLoadingRepos}
              />
            </div>
          </div>
        )}

        {/* Repo list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingRepos ? (
            <RepoListSkeleton />
          ) : fetchError ? (
            <div className="p-6">
              <div className="bg-forge-heat-critical/10 border border-forge-heat-critical/30 rounded-lg p-4">
                <p className="text-forge-heat-critical text-sm font-semibold mb-1">Could not load repositories</p>
                <p className="text-forge-ash text-xs">{fetchError}</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-forge-ash text-sm">
              No repositories match "{search}"
            </div>
          ) : (
            <ul className="py-1">
              {filtered.map((repo) => (
                <li key={repo.id}>
                  <button
                    onClick={() => { setSelected(repo); setBranch(repo.default_branch); setSaveError(null) }}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                      selected?.id === repo.id
                        ? 'bg-forge-ember/10 border-l-2 border-forge-ember'
                        : 'hover:bg-forge-basalt border-l-2 border-transparent',
                    ].join(' ')}
                  >
                    <img
                      src={repo.owner.avatar_url}
                      alt={repo.owner.login}
                      className="w-7 h-7 rounded-full flex-shrink-0 ring-1 ring-forge-border"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-forge-primary truncate">
                          <span className="text-forge-ash">{repo.owner.login}/</span>
                          {repo.name}
                        </span>
                        {repo.private && (
                          <span className="text-[10px] text-forge-ash border border-forge-border rounded px-1.5 py-0.5 flex-shrink-0 uppercase tracking-wider">
                            Private
                          </span>
                        )}
                      </div>
                      {repo.description && (
                        <p className="text-xs text-forge-ash truncate mt-0.5">{repo.description}</p>
                      )}
                    </div>
                    {selected?.id === repo.id && (
                      <svg className="w-4 h-4 text-forge-ember flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {selected && (
          <div className="border-t border-forge-border px-4 py-4 flex-shrink-0 space-y-3 animate-fade-in">
            {saveError && <p className="text-forge-heat-critical text-xs">{saveError}</p>}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-[10px] text-forge-gold uppercase tracking-wider mb-1 font-semibold">
                  Branch
                </label>
                <input
                  type="text"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="w-full bg-forge-void border border-forge-border rounded-md px-3 py-1.5 text-sm text-forge-primary font-mono
                             focus:outline-none focus:border-forge-gold transition-colors"
                />
              </div>
              <div className="pt-4 flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg bg-forge-basalt border border-forge-slate text-sm text-forge-ash hover:bg-forge-slate hover:text-forge-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnect}
                  disabled={isSaving}
                  className="px-4 py-2 rounded-lg bg-forge-ember hover:bg-forge-molten text-white text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Connecting…' : 'Connect'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function RepoListSkeleton() {
  return (
    <div className="py-2 px-4 space-y-3 animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-forge-slate flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 bg-forge-slate rounded w-2/5" />
            <div className="h-3 bg-forge-slate rounded w-3/5" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Empty Vault ──────────────────────────────────────────────────────────────

function EmptyVault({ onAddRepo, onAddFolder, supportsLocal }: {
  onAddRepo:    () => void
  onAddFolder:  () => void
  supportsLocal: boolean
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 py-24 animate-fade-in">
      <div className="w-16 h-16 rounded-2xl border border-forge-border flex items-center justify-center mb-6">
        <svg className="w-8 h-8 text-forge-border" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
          />
        </svg>
      </div>
      <h2 className="text-lg font-display font-semibold text-forge-primary mb-2">Vault is empty</h2>
      <p className="text-sm text-forge-ash max-w-xs leading-relaxed mb-8">
        Connect a GitHub repository or add a local folder to start reading Markdown documents.
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={onAddRepo}
          className="px-5 py-2.5 bg-forge-ember hover:bg-forge-molten text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-forge-ember/25"
        >
          Connect Repo
        </button>
        {supportsLocal && (
          <button
            onClick={onAddFolder}
            className="px-5 py-2.5 bg-forge-basalt border border-forge-border hover:border-forge-gold text-forge-primary rounded-lg text-sm font-semibold transition-colors"
          >
            Add Local Folder
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Global Vault ─────────────────────────────────────────────────────────────

export function GlobalVault() {
  const { repos, removeSource, updateLastAccessed,
          addLocalFolder, supportsLocalFS }         = useRepos()
  const { selectSource }                            = useAppStore()
  const [showModal, setShowModal]                   = useState(false)
  const [addingFolder, setAddingFolder]             = useState(false)
  const [folderError, setFolderError]               = useState<string | null>(null)

  const handleSelectSource = async (source: ConnectedSource) => {
    await updateLastAccessed(source)
    selectSource(source)
  }

  const handleAddFolder = async () => {
    setAddingFolder(true)
    setFolderError(null)
    try {
      await addLocalFolder()
    } catch (err) {
      setFolderError(err instanceof Error ? err.message : 'Failed to add folder.')
    } finally {
      setAddingFolder(false)
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-forge-border/50 flex-shrink-0">
        <div>
          <h1 className="text-lg font-display font-semibold text-forge-primary">The Vault</h1>
          <p className="text-xs text-forge-ash mt-0.5">
            {repos.length === 0
              ? 'No sources connected'
              : `${repos.length} ${repos.length === 1 ? 'source' : 'sources'} connected`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {supportsLocalFS && (
            <button
              onClick={handleAddFolder}
              disabled={addingFolder}
              className="flex items-center gap-2 px-4 py-2 bg-forge-basalt border border-forge-border hover:border-forge-gold text-forge-primary rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
              {addingFolder ? 'Opening…' : 'Add Folder'}
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-forge-ember hover:bg-forge-molten text-white rounded-lg text-sm font-semibold transition-colors shadow-md shadow-forge-ember/25"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Connect Repo
          </button>
        </div>
      </div>

      {/* Folder error banner */}
      {folderError && (
        <div className="mx-8 mt-4 bg-forge-heat-critical/10 border border-forge-heat-critical/30 rounded-lg px-4 py-3 flex items-start justify-between gap-3 animate-fade-in">
          <p className="text-forge-heat-critical text-xs leading-relaxed">{folderError}</p>
          <button onClick={() => setFolderError(null)} className="text-forge-ash hover:text-forge-primary flex-shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-y-auto">
        {repos.length === 0 ? (
          <EmptyVault
            onAddRepo={() => setShowModal(true)}
            onAddFolder={handleAddFolder}
            supportsLocal={supportsLocalFS}
          />
        ) : (
          <div className="p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {repos.map((source) => (
              <RepoCard
                key={source.id}
                repo={source}
                onSelect={() => void handleSelectSource(source)}
                onRemove={() => removeSource(source)}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && <AddRepoModal onClose={() => setShowModal(false)} />}
    </div>
  )
}
